import React, { useEffect } from "react";
import {
  ShieldCheckIcon,
  EyeIcon,
  LockClosedIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/outline";

const PrivacyPolicy: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-orange via-orange-500 to-accent-red text-white pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="w-20 h-20 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <ShieldCheckIcon className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Privacy Policy
            </h1>
            <p className="text-xl opacity-95 mb-2">
              How we protect and use your information
            </p>
            <p className="text-lg opacity-80">Last updated: January 2024</p>
          </div>
        </div>
      </section>

      {/* Privacy Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {/* Information We Collect */}
            <div className="bg-gray-50 p-8 rounded-2xl">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                  <EyeIcon className="w-6 h-6 text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                  1. Information We Collect
                </h2>
              </div>
              <p className="text-gray-600 mb-6 leading-relaxed">
                When you interact with JSONPLAY, we may collect certain
                information to provide you with better services:
              </p>
              <div className="space-y-4">
                <div className="bg-white p-4 rounded-lg border-l-4 border-blue-500">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Personal Information
                  </h4>
                  <p className="text-gray-600">
                    Name, email address, phone number, and company details when
                    you contact us or request our services.
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg border-l-4 border-green-500">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Communication Data
                  </h4>
                  <p className="text-gray-600">
                    Messages, feedback, and correspondence you send to us.
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg border-l-4 border-yellow-500">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Usage Information
                  </h4>
                  <p className="text-gray-600">
                    How you interact with our website, including pages visited
                    and time spent.
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg border-l-4 border-purple-500">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Technical Data
                  </h4>
                  <p className="text-gray-600">
                    IP address, browser type, device information, and cookies.
                  </p>
                </div>
              </div>
            </div>

            {/* How We Use Information */}
            <div className="bg-gray-50 p-8 rounded-2xl">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                  <DocumentTextIcon className="w-6 h-6 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                  2. How We Use Your Information
                </h2>
              </div>
              <p className="text-gray-600 mb-6 leading-relaxed">
                We use the collected information for the following purposes:
              </p>
              <ul className="space-y-3">
                {[
                  "To respond to your inquiries and provide customer support",
                  "To deliver our development services and communicate about projects",
                  "To improve our website and services",
                  "To send you relevant updates about our services (with your consent)",
                  "To comply with legal obligations and protect our rights",
                ].map((item, index) => (
                  <li key={index} className="flex items-start">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Information Sharing */}
            <div className="bg-gray-50 p-8 rounded-2xl">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mr-4">
                  <UserGroupIcon className="w-6 h-6 text-yellow-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                  3. Information Sharing
                </h2>
              </div>
              <p className="text-gray-600 mb-6 leading-relaxed">
                We do not sell, trade, or otherwise transfer your personal
                information to third parties except in the following
                circumstances:
              </p>
              <div className="space-y-4">
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Service Providers
                  </h4>
                  <p className="text-gray-600">
                    We may share information with trusted third-party service
                    providers who assist us in operating our business.
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Legal Requirements
                  </h4>
                  <p className="text-gray-600">
                    We may disclose information when required by law or to
                    protect our rights and safety.
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Business Transfers
                  </h4>
                  <p className="text-gray-600">
                    In the event of a merger, acquisition, or asset sale, user
                    information may be transferred.
                  </p>
                </div>
              </div>
            </div>

            {/* Data Security */}
            <div className="bg-gray-50 p-8 rounded-2xl">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                  <LockClosedIcon className="w-6 h-6 text-red-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                  4. Data Security
                </h2>
              </div>
              <p className="text-gray-600 leading-relaxed">
                We implement appropriate technical and organizational measures
                to protect your personal information against unauthorized
                access, alteration, disclosure, or destruction. However, no
                method of transmission over the internet is 100% secure.
              </p>
            </div>

            {/* Additional Sections */}
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-6 rounded-xl shadow-sm border">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  5. Cookies and Tracking
                </h3>
                <p className="text-gray-600 mb-4 text-sm">
                  Our website uses cookies and similar tracking technologies to
                  enhance your browsing experience.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
                    <span className="text-gray-600">
                      <strong>Essential:</strong> Required for basic
                      functionality
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
                    <span className="text-gray-600">
                      <strong>Analytics:</strong> Help us understand site usage
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-1.5 h-1.5 bg-purple-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
                    <span className="text-gray-600">
                      <strong>Preference:</strong> Remember your settings
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  6. Your Rights
                </h3>
                <p className="text-gray-600 mb-4 text-sm">
                  Depending on your location, you may have these rights:
                </p>
                <ul className="space-y-2 text-sm">
                  {[
                    "Access to your personal information",
                    "Correction of inaccurate data",
                    "Deletion of your personal information",
                    "Restriction of processing",
                    "Data portability",
                    "Objection to processing",
                  ].map((right, index) => (
                    <li key={index} className="flex items-start">
                      <div className="w-1.5 h-1.5 bg-primary-orange rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
                      <span className="text-gray-600">{right}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Additional Policy Sections */}
            <div className="space-y-6">
              {[
                {
                  title: "7. Third-Party Links",
                  content:
                    "Our website may contain links to third-party sites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies.",
                },
                {
                  title: "8. Children's Privacy",
                  content:
                    "Our services are not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13.",
                },
                {
                  title: "9. International Data Transfers",
                  content:
                    "Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place for such transfers.",
                },
                {
                  title: "10. Updates to This Policy",
                  content:
                    "We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on our website and updating the 'Last updated' date.",
                },
              ].map((section, index) => (
                <div
                  key={index}
                  className="bg-white p-6 rounded-xl shadow-sm border"
                >
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    {section.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {section.content}
                  </p>
                </div>
              ))}
            </div>

            {/* Contact Section */}
            <div className="bg-gradient-to-br from-primary-orange to-accent-red text-white p-8 rounded-2xl">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-4">
                  <ChatBubbleLeftRightIcon className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-bold">11. Contact Us</h2>
              </div>
              <p className="mb-6 opacity-95 leading-relaxed">
                If you have questions about this Privacy Policy or our data
                practices, please contact us:
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-white bg-opacity-10 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">Email</h4>
                  <a
                    href="mailto:hello@jsonplay.com"
                    className="hover:underline"
                  >
                    hello@jsonplay.com
                  </a>
                </div>
                <div className="bg-white bg-opacity-10 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">Phone</h4>
                  <a href="tel:+917017353583" className="hover:underline">
                    +91 70173 53583
                  </a>
                </div>
                <div className="bg-white bg-opacity-10 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">WhatsApp</h4>
                  <a
                    href="https://wa.me/917017353583?text=Hi! I have questions about your Privacy Policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                    onClick={() => {
                      if (typeof window !== 'undefined' && (window as any).gtag_report_conversion) {
                        return (window as any).gtag_report_conversion('https://wa.me/917017353583?text=Hi! I have questions about your Privacy Policy');
                      }
                      return true;
                    }}
                  >
                    Chat with us
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
